/**
 * Audio/video device selection.
 *
 * Choices are remembered in localStorage and applied to every getUserMedia call
 * and, where the browser supports it, to playback of remote audio via setSinkId.
 */

const STORAGE_KEY = 'webchat.devices';

const DEFAULTS = {
  audioInput: '',
  videoInput: '',
  audioOutput: '',
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  videoQuality: '720p',
  /**
   * Master playback level, 0..1, applied on top of each feed's own volume - the
   * people in the room and the jukebox alike - and the mute that silences all of
   * it at once. A switch of its own rather than a level of nought, because it is
   * the half that still works where a browser refuses to set a level at all
   * (see canSetVolume), and because coming back from muted should return you to
   * the level you had.
   */
  masterVolume: 1,
  masterMuted: false,
  /**
   * The room's jukebox, on the same 0..1 scale and under the same master level.
   * Its own, because music behind a conversation wants to be quieter than the
   * conversation, and setting that once should not mean turning people down too.
   *
   * `jukeboxMuted` is a separate switch rather than a level of zero, because it
   * is the only one of the two that works everywhere - see canSetVolume() - and
   * because coming back from muted should return you to the level you had.
   */
  jukeboxVolume: 0.6,
  jukeboxMuted: false,
  /**
   * How the jukebox and the transcript divide the space they share: the
   * jukebox's share of it, 0..1. Dragged on the bar between the two.
   */
  jukeboxSplit: 0.5,
  /**
   * Whether this session takes part in the room's jukebox at all.
   *
   * Off hides the panel, tears down any player already running, and - because
   * the YouTube frame and the script that drives it are only ever fetched when a
   * player is mounted - means nothing is requested from youtube.com for the rest
   * of the session. That is the point of it: the jukebox is the one part of this
   * client that talks to anybody else, and not everyone wants it to.
   *
   * Null rather than true, because the answer nobody has given depends on the
   * device: a phone screen that has to hold a video, a transcript and a keyboard
   * starts without the video. Pressing the button writes a real boolean, and
   * from then on the choice is the choice on every device. See wantsYouTube().
   */
  showYouTube: null,
  /**
   * How much of the axis it shares with the transcript the camera stage may
   * take, 0..1 - dragged on the bar along its edge.
   *
   * Null until somebody drags it, and deliberately so: the stylesheet sets a
   * different share for each arrangement, since a stage above the chat and a
   * stage beside it are not competing for the same thing. Storing a number here
   * overrides all of them, which is right once it has been asked for and wrong
   * before.
   */
  stageShare: null,
  /**
   * Push to talk: the microphone stays muted and opens only while the Talk
   * button is held. Replaces the mute toggle rather than sitting beside it -
   * the two are the same decision made in opposite directions.
   */
  pushToTalk: false,
  /**
   * A short chime when somebody names you or sends you a private message.
   *
   * On by default: being mentioned is the one thing in a chat room that is
   * addressed to you personally, and a room you are not looking at is exactly
   * where you would miss it. It is still subject to the master mute, since that
   * is the button people press to make the application stop making noise.
   */
  notificationSound: true,
};

export const QUALITY = {
  '360p': { width: 640, height: 360, frameRate: 24 },
  '480p': { width: 854, height: 480, frameRate: 30 },
  '720p': { width: 1280, height: 720, frameRate: 30 },
  '1080p': { width: 1920, height: 1080, frameRate: 30 },
};

function read() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return { ...DEFAULTS, ...(stored && typeof stored === 'object' ? stored : {}) };
  } catch {
    return { ...DEFAULTS };
  }
}

let settings = read();

export function getSettings() {
  return { ...settings };
}

export function updateSettings(patch) {
  settings = { ...settings, ...patch };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* storage may be unavailable; keep the in-memory value */
  }
  return getSettings();
}

/** True when the browser can route audio output to a chosen device. */
export function canChooseOutput() {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

let volumeSettable = null;

/**
 * True when setting a playback level in software does anything.
 *
 * iOS refuses it outright - on iPhone and iPad the hardware buttons are the only
 * volume there is, and `element.volume` silently snaps back to 1 - and a few
 * other mobile browsers do the same. There is no flag to ask, so this asks the
 * only way there is: set a level on a media element nothing can hear and read it
 * back. Muting is a separate switch and does work everywhere, which is why the
 * interface falls back to it rather than to nothing.
 */
export function canSetVolume() {
  if (volumeSettable !== null) return volumeSettable;
  if (typeof Audio !== 'function') return (volumeSettable = false);
  try {
    const probe = new Audio();
    probe.volume = 0.5;
    volumeSettable = probe.volume === 0.5;
  } catch {
    volumeSettable = false;
  }
  return volumeSettable;
}

/**
 * Lists available devices, grouped by kind.
 *
 * Labels are only populated once the user has granted a media permission, so
 * placeholders are substituted to keep the menu usable beforehand.
 */
export async function listDevices() {
  const noneHidden = { audioInput: 0, videoInput: 0, audioOutput: 0 };
  const empty = {
    audioInput: [],
    videoInput: [],
    audioOutput: [],
    labelsHidden: false,
    hidden: { ...noneHidden },
  };
  if (!navigator.mediaDevices?.enumerateDevices) return empty;

  let devices;
  try {
    devices = await navigator.mediaDevices.enumerateDevices();
  } catch {
    return empty;
  }

  const grouped = { audioInput: [], videoInput: [], audioOutput: [] };
  /**
   * Devices present but withheld, per kind. Firefox grants enumeration one kind
   * at a time - a microphone permission leaves every camera with an empty
   * deviceId - so "the camera list is empty" and "this machine has no camera"
   * have to be told apart.
   */
  const hidden = { ...noneHidden };
  let labelsHidden = false;
  const counters = { audioinput: 0, videoinput: 0, audiooutput: 0 };
  const names = { audioinput: 'Microphone', videoinput: 'Camera', audiooutput: 'Speaker' };

  for (const device of devices) {
    const bucket =
      device.kind === 'audioinput'
        ? 'audioInput'
        : device.kind === 'videoinput'
          ? 'videoInput'
          : device.kind === 'audiooutput'
            ? 'audioOutput'
            : null;
    if (!bucket) continue;
    counters[device.kind] += 1;
    if (!device.label) labelsHidden = true;
    // Before a permission has been granted, browsers hand out placeholder
    // entries with an empty deviceId. Selecting one would be meaningless, and it
    // would collide with the "System default" option, so leave them out.
    if (!device.deviceId) {
      hidden[bucket] += 1;
      continue;
    }
    grouped[bucket].push({
      deviceId: device.deviceId,
      label: device.label || `${names[device.kind]} ${counters[device.kind]}`,
    });
  }
  return { ...grouped, labelsHidden, hidden };
}

/**
 * Asks for a one-time permission purely to unlock device enumeration: until
 * something has been granted, browsers hide device ids and labels, which is why
 * the pickers are unusable before you first broadcast. Every stream is stopped
 * immediately - nothing is published.
 *
 * The microphone and the camera are asked for together, because Firefox exposes
 * device ids per permitted kind: with a microphone-only grant every camera keeps
 * an empty deviceId and the camera picker stays empty, however many cameras are
 * attached. Chrome reveals all kinds after any single grant, so this only shows
 * up in Firefox.
 */
export async function requestDeviceAccess() {
  if (!navigator.mediaDevices?.getUserMedia) return false;

  const grab = async (constraints) => {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    for (const track of stream.getTracks()) track.stop();
    return true;
  };

  try {
    return await grab({ audio: true, video: true });
  } catch (err) {
    // A refusal is final: asking again one kind at a time would just nag.
    if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') return false;
  }

  // Otherwise one of the two is missing or already claimed by another
  // application, so ask for each on its own rather than losing both.
  let granted = false;
  for (const constraints of [{ audio: true }, { video: true }]) {
    try {
      granted = (await grab(constraints)) || granted;
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') break;
    }
  }
  return granted;
}

/**
 * Forgets one saved device id, by settings key ('audioInput', 'videoInput' or
 * 'audioOutput').
 *
 * Used when a capture has just failed with that device: the id is then known
 * bad, which pruneMissingDevices() cannot establish on its own - it keeps the
 * saved preference whenever enumeration comes back empty, and an empty list is
 * exactly what a browser withholding ids for that kind produces.
 */
export function clearDevice(key) {
  if (!(key in DEFAULTS) || !settings[key]) return getSettings();
  return updateSettings({ [key]: '' });
}

/** Constraints for the microphone, honouring the saved selection. */
export function audioConstraints() {
  const { audioInput, echoCancellation, noiseSuppression, autoGainControl } = settings;
  return {
    audio: {
      ...(audioInput ? { deviceId: { exact: audioInput } } : {}),
      echoCancellation,
      noiseSuppression,
      autoGainControl,
    },
  };
}

/**
 * True while the screen is taller than it is wide.
 *
 * screen.orientation is preferred because the camera turns with the *device*,
 * not with the window - they differ under Android split-screen, where the window
 * can be narrow while the device is on its side.
 *
 * Beware when testing: browser device emulation reports screen.orientation from
 * the host display, not the emulated viewport, so this path reads landscape in a
 * portrait emulator. Only a real device tells the truth here.
 */
export function isPortrait() {
  if (typeof screen !== 'undefined' && typeof screen.orientation?.type === 'string') {
    return screen.orientation.type.startsWith('portrait');
  }
  if (typeof matchMedia === 'function') return matchMedia('(orientation: portrait)').matches;
  return false;
}

/**
 * True on a device whose camera turns with the screen - a phone or tablet.
 *
 * A desktop monitor in portrait is taller than it is wide too, but its webcam is
 * still landscape, so orientation must not be read from the screen shape alone.
 */
function cameraFollowsScreen() {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

/** True when the capture should be requested portrait rather than landscape. */
export function wantsPortraitCapture() {
  return cameraFollowsScreen() && isPortrait();
}

/**
 * How far the device is turned from its natural upright position, in degrees.
 *
 * screen.orientation.angle is the modern reading; window.orientation is what
 * older iOS Safari offers, and it reports -90 where the standard says 270.
 */
export function deviceAngle() {
  if (typeof screen !== 'undefined' && typeof screen.orientation?.angle === 'number') {
    return ((screen.orientation.angle % 360) + 360) % 360;
  }
  if (typeof window !== 'undefined' && typeof window.orientation === 'number') {
    return ((window.orientation % 360) + 360) % 360;
  }
  return 0;
}

/**
 * What a publisher tells the room about how it is holding its camera.
 *
 * Only two facts, both of which this device knows for certain: which way it is
 * turned, and whether its camera turns with it at all. The rotation itself is
 * worked out by each viewer, from the frame size it actually decoded - see
 * rotationForFrame().
 */
export function publisherOrientation() {
  const handheld = cameraFollowsScreen();
  return { angle: deviceAngle(), handheld, sensorFrames: handheld && sendsSensorFrames() };
}

/**
 * True when this browser encodes camera frames in the sensor's own orientation
 * and leaves the rotation to be signalled separately.
 *
 * When urn:3gpp:video-orientation is not negotiated - and Firefox never
 * negotiates it - libwebrtc rotates the frames itself before encoding, so
 * Chrome and Firefox publishers arrive upright and must not be turned again.
 * WebKit does not do that, which is why an iPhone's picture arrives on its side
 * and needs turning here.
 *
 * Every browser on iOS and iPadOS is WebKit, so the platform is the test rather
 * than the browser name.
 */
function sendsSensorFrames() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent ?? '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS reports itself as a Mac; a touch-capable one is really an iPad.
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1;
}

/**
 * A quarter turn clockwise: what an upright iPhone's picture needs to come out
 * the right way up. Measured on a real device, not derived.
 */
const UPRIGHT_SENSOR_OFFSET = 90;

/** Cached: a browser's capabilities do not change while the page is open. */
let honoursOrientationExtension = null;

/**
 * Whether this browser turns a rotated picture upright by itself.
 *
 * It does so when it negotiates urn:3gpp:video-orientation, which is exactly
 * what its receiver capabilities say. Chrome lists the extension and rotates;
 * Firefox does not list it and does not rotate. Asking the browser what it can
 * do beats naming browsers - and getting this wrong is not harmless: turning a
 * picture that has already been turned leaves it a quarter of the way round the
 * other way, which is what happened to Chrome viewers.
 *
 * The negotiated parameters of an individual consumer would be more precise
 * still, but they are derived from these same capabilities.
 */
export function viewerRotatesByItself() {
  if (honoursOrientationExtension !== null) return honoursOrientationExtension;
  try {
    const capabilities = globalThis.RTCRtpReceiver?.getCapabilities?.('video');
    if (capabilities?.headerExtensions) {
      honoursOrientationExtension = capabilities.headerExtensions.some(
        (extension) => extension.uri === 'urn:3gpp:video-orientation',
      );
      return honoursOrientationExtension;
    }
  } catch {
    /* fall through to the engine check */
  }
  // No capability list to ask: Gecko is the engine that does not do this, and
  // "like Gecko" in other browsers' user agents has no slash after it.
  const ua = globalThis.navigator?.userAgent ?? '';
  honoursOrientationExtension = !/Gecko\/|Firefox\//.test(ua);
  return honoursOrientationExtension;
}

/**
 * How far a viewer must rotate a publisher's video to bring it upright.
 *
 * A phone camera records in the sensor's own orientation, so turning the phone
 * does not turn the picture; the rotation is meant to travel separately, in the
 * urn:3gpp:video-orientation RTP header extension. Firefox implements that
 * extension in neither direction - it strips it from its SDP answer - so for a
 * Firefox viewer the rotation has to be carried by the application and applied
 * in CSS. iOS Safari publishes exactly this way, which is why an iPhone's video
 * arrives on its side there.
 *
 * The frame's own shape is deliberately NOT consulted. iOS hands over a
 * portrait-shaped frame containing a sideways picture (measured: 360x640 from an
 * upright iPhone), so a shape that agrees with how the phone is held proves
 * nothing about which way up the content is. An earlier version inferred "already
 * upright" from exactly that agreement, and so rotated nothing at all.
 *
 * What is left is geometry. The picture is fixed to the phone's body, so the
 * correction is a constant plus however far the body has been turned:
 *
 *     angle   0  upright               ->  90   confirmed on a real iPhone
 *     angle  90  turned anti-clockwise -> 180
 *     angle 180  upside down           -> 270
 *     angle 270  turned clockwise      ->   0
 *
 * Only the upright case has been measured. The other three follow from it by
 * symmetry, and if one of those comes out wrong the constant is not to blame -
 * the sign of `angle` is.
 *
 * Nothing is turned in a browser that turns pictures itself - see
 * viewerRotatesByItself(). This correction exists to stand in for a browser that
 * ignores the rotation, not to add a second one on top of a browser that has
 * already applied it.
 *
 * `sensorFrames` distinguishes publishers whose frames arrive already upright
 * (libwebrtc turns them itself when the RTP extension is absent, so Chrome and
 * Firefox publishers must be left alone) from WebKit, which does not. Only an
 * explicit `false` disables the rotation: a publisher running an older build
 * sends no such flag at all, and treating that silence as "never rotate" is what
 * switched this off for everyone mid-call.
 */
export function rotationForFrame({ angle = 0, handheld = false, sensorFrames } = {}) {
  if (!handheld || sensorFrames === false) return 0;
  if (viewerRotatesByItself()) return 0;
  const turned = Number(angle);
  const normalised = Number.isFinite(turned) ? ((turned % 360) + 360) % 360 : 0;
  return (UPRIGHT_SENSOR_OFFSET + normalised) % 360;
}

/**
 * Constraints for the camera, honouring the saved selection and quality.
 *
 * The quality table is written landscape, and on a phone held upright the
 * request is turned to match. WebRTC can carry the device rotation as an RTP
 * header extension (urn:3gpp:video-orientation, which the router does offer) and
 * let the viewer rotate on playback, but Firefox does not send it - so a portrait
 * phone's picture arrived at everyone else lying on its side. Asking the capture
 * pipeline for portrait dimensions produces an upright frame instead, which needs
 * no rotation metadata and is therefore right in every browser.
 */
export function videoConstraints() {
  const { videoInput, videoQuality } = settings;
  const quality = QUALITY[videoQuality] ?? QUALITY['720p'];
  const portrait = wantsPortraitCapture();
  return {
    video: {
      ...(videoInput ? { deviceId: { exact: videoInput } } : {}),
      width: { ideal: portrait ? quality.height : quality.width },
      height: { ideal: portrait ? quality.width : quality.height },
      frameRate: { ideal: quality.frameRate },
    },
  };
}

/**
 * Playback level for a feed: its own volume scaled by the master level.
 * `own` is 0..1; returns the value to assign to element.volume.
 */
export function effectiveVolume(own = 1) {
  const master = Number(settings.masterVolume);
  const scaled = (Number.isFinite(master) ? master : 1) * (Number.isFinite(own) ? own : 1);
  return Math.max(0, Math.min(1, scaled));
}

/**
 * True when everything incoming is muted from the media bar.
 *
 * Kept apart from the level rather than folded into effectiveVolume(), because
 * mute and volume are two different things everywhere they are finally applied:
 * a media element has `muted` beside `volume`, and the YouTube player has
 * `mute()` beside `setVolume()`. Silencing by way of a level of nought is the
 * one way of doing it that neither of them honours reliably.
 */
export function masterMuted() {
  return settings.masterMuted === true;
}

/**
 * Routes a media element to the chosen output device.
 * Silently ignored where setSinkId is unsupported - Safari, and Firefox before
 * 116; current Firefox does support it, so canChooseOutput() decides rather than
 * any assumption about the browser.
 */
export async function applyOutput(element) {
  const { audioOutput } = settings;
  if (!audioOutput || !canChooseOutput() || typeof element?.setSinkId !== 'function') return false;
  try {
    await element.setSinkId(audioOutput);
    return true;
  } catch {
    return false;
  }
}

/** The saved ids each capture pins as `exact`, in the order to suspect them. */
const PINNED_DEVICES = {
  mic: ['audioInput'],
  // Turning on the camera captures audio as well, so both ids are pinned and
  // either can be the stale one.
  camera: ['videoInput', 'audioInput'],
  screen: [],
};

/**
 * Recovers from a capture that failed because a saved device id is no longer
 * valid: forgets the offending id and retries through `capture`, returning its
 * stream. Returns null when there was nothing to forget or no retry worked.
 *
 * Without this the failure is a dead end in Firefox, which rotates device ids
 * between sessions unless the permission was made permanent. A remembered id
 * goes stale, the stale id makes getUserMedia reject with OverconstrainedError,
 * and pruneMissingDevices() cannot clear it: ids for a kind that has not been
 * granted are withheld, so there is an empty list and nothing to compare
 * against. The picker then offers only "System default", so the advice to pick
 * another device cannot be followed either.
 *
 * OverconstrainedError does not say which constraint failed, and a camera
 * capture pins the microphone too, so the ids are dropped one at a time with a
 * retry after each. Any id the browser has just listed is known good and kept,
 * which is what stops a working microphone choice being thrown away because the
 * camera id went stale.
 */
export async function retryWithoutSavedDevice(source, err, capture) {
  if (err?.name !== 'OverconstrainedError' && err?.name !== 'NotFoundError') return null;
  const suspects = (PINNED_DEVICES[source] ?? []).filter((key) => settings[key]);
  if (suspects.length === 0) return null;

  const available = await listDevices();
  for (const key of suspects) {
    const listed = available[key] ?? [];
    if (listed.length > 0 && listed.some((d) => d.deviceId === settings[key])) continue;
    clearDevice(key);
    try {
      return await capture(source);
    } catch {
      // Still failing, so suspect the next pinned id.
    }
  }
  return null;
}

/**
 * Verifies that saved device ids still exist, clearing any that have been
 * unplugged so the next capture falls back to the system default instead of
 * failing with OverconstrainedError.
 */
export async function pruneMissingDevices() {
  const available = await listDevices();
  const patch = {};
  for (const [key, list] of [
    ['audioInput', available.audioInput],
    ['videoInput', available.videoInput],
    ['audioOutput', available.audioOutput],
  ]) {
    const chosen = settings[key];
    if (chosen && list.length > 0 && !list.some((d) => d.deviceId === chosen)) patch[key] = '';
  }
  if (Object.keys(patch).length > 0) updateSettings(patch);
  return available;
}

export default {
  getSettings,
  updateSettings,
  listDevices,
  clearDevice,
  audioConstraints,
  videoConstraints,
  applyOutput,
};
